import { BarChart3, CalendarDays, CirclePlus, CreditCard, Home, ReceiptText, Settings, Target, WalletCards } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";
import { useApp } from "../context/AppContext";
import { QuickAddSheet } from "./QuickAddSheet";
import { ConnectivityStatus } from "./PwaStatus";

const nav = [
  { to: "/", label: "Home", icon: Home, mobile: true },
  { to: "/transactions", label: "Transactions", icon: ReceiptText, mobile: true },
  { to: "/bills", label: "Bills", icon: CalendarDays, mobile: true },
  { to: "/accounts", label: "Accounts", icon: WalletCards, mobile: false },
  { to: "/goals", label: "Goals", icon: Target, mobile: false },
  { to: "/settings", label: "Settings", icon: Settings, mobile: true }
];

function AppLink({ to, currentPath, navigate, children, className = "", label }: { to: string; currentPath: string; navigate: (path: string) => void; children: ReactNode; className?: string; label?: string }) {
  function follow(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  }
  return <a href={to} onClick={follow} className={`${className} ${currentPath === to ? "active" : ""}`.trim()} aria-label={label}>{children}</a>;
}

export function AppShell({ currentPath, navigate, children }: { currentPath: string; navigate: (path: string) => void; children: ReactNode }) {
  const { openQuickAdd, quickAddOpen, closeQuickAdd, toast } = useApp();
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <AppLink to="/" currentPath={currentPath} navigate={navigate} className="sidebar-brand" label="Dayah finance home">
          <span className="brand-mark brand-mark--small">D<span /></span>
          <span><strong>Dayah finance</strong><small>Personal ledger</small></span>
        </AppLink>
        <nav aria-label="Main navigation">
          {nav.map(({ to, label, icon: Icon }) => <AppLink key={to} to={to} currentPath={currentPath} navigate={navigate}><Icon size={19} /><span>{label}</span></AppLink>)}
        </nav>
        <div className="sidebar-foot"><BarChart3 size={18} /><span><strong>Private by design</strong><small>Your data stays in your database.</small></span></div>
      </aside>
      <div className="app-stage">
        <main className="page-stage">{children}</main>
        <button className="quick-add-fab" onClick={openQuickAdd} aria-label="Add transaction"><CirclePlus /><span>Add transaction</span></button>
        <nav className="bottom-nav" aria-label="Mobile navigation">
          {nav.filter((item) => item.mobile).slice(0, 2).map(({ to, label, icon: Icon }) => <AppLink key={to} to={to} currentPath={currentPath} navigate={navigate}><Icon /><span>{label}</span></AppLink>)}
          <button onClick={openQuickAdd} aria-label="Add transaction"><CirclePlus /></button>
          {nav.filter((item) => item.mobile).slice(2).map(({ to, label, icon: Icon }) => <AppLink key={to} to={to} currentPath={currentPath} navigate={navigate}><Icon /><span>{label}</span></AppLink>)}
        </nav>
      </div>
      {quickAddOpen && <QuickAddSheet onClose={closeQuickAdd} />}
      <ConnectivityStatus />
      {toast && <div className="toast" role="status"><CreditCard size={18} />{toast}</div>}
    </div>
  );
}
